import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {MutationObserver | undefined} */
  productCardInteractionMutationObserver;

  /** @type {Map<Element, number>} */
  productCardInteractionIntervals = new Map();

  /** @type {Map<Element, number>} */
  productCardTouchDelays = new Map();

  /** @type {WeakSet<Element>} */
  productCardTouchActivated = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.#initializeProductCardInteractions();
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.productCardInteractionMutationObserver?.disconnect();
    this.querySelectorAll('.product-grid__item').forEach((card) => this.#removeProductCardInteractions(card));
  }

  /**
   * Prepares product-card galleries for hover and sustained-touch movement.
   */
  #initializeProductCardInteractions() {
    const grid = this.refs.grid;
    if (!grid) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.setAttribute('data-kult-card-interaction', 'reduced');
      return;
    }

    this.setAttribute('data-kult-card-interaction', 'ready');
    this.#prepareProductCardInteractions(grid);

    this.productCardInteractionMutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof Element) {
            this.#prepareProductCardInteractions(node);
          }
        }

        for (const node of mutation.removedNodes) {
          if (!(node instanceof Element)) continue;

          const removedCards = node.matches('.product-grid__item')
            ? [node]
            : Array.from(node.querySelectorAll('.product-grid__item'));
          for (const card of removedCards) {
            this.#removeProductCardInteractions(card);
          }
        }
      }
    });

    this.productCardInteractionMutationObserver.observe(grid, { childList: true });
  }

  /**
   * @param {Element} container
   */
  #prepareProductCardInteractions(container) {
    const cards = container.matches('.product-grid__item')
      ? [container]
      : Array.from(container.querySelectorAll('.product-grid__item'));

    for (const card of cards) {
      if (card.hasAttribute('data-kult-card-interaction-ready')) continue;

      card.setAttribute('data-kult-card-interaction-ready', '');
      card.querySelector('.card-gallery slideshow-component')?.setAttribute('data-kult-card-interaction-managed', '');
      card.addEventListener('pointerenter', this.#handleProductCardPointerEnter, true);
      card.addEventListener('pointerleave', this.#handleProductCardPointerLeave, true);
      card.addEventListener('pointerdown', this.#handleProductCardPointerDown);
      card.addEventListener('pointerup', this.#handleProductCardPointerEnd);
      card.addEventListener('pointercancel', this.#handleProductCardPointerEnd);
      card.addEventListener('click', this.#handleProductCardClick, true);
      card.addEventListener('contextmenu', this.#handleProductCardContextMenu);
    }
  }

  /**
   * @param {Element} card
   */
  #startProductCardInteraction(card) {
    const slideshow = /** @type {HTMLElement & { current?: number, slides?: Element[], select?: (input: number, event?: Event, options?: { animate?: boolean }) => void, suspend?: () => void }} */ (
      card.querySelector('.card-gallery slideshow-component')
    );
    if (!slideshow || slideshow.querySelectorAll('slideshow-slide:not([hidden])').length <= 1) return;
    if (this.productCardInteractionIntervals.has(card)) return;

    slideshow.setAttribute('data-kult-card-autoplay', '');
    slideshow.setAttribute('aria-live', 'off');
    slideshow.suspend?.();
    this.#advanceProductCardSlide(slideshow);

    const interval = window.setInterval(() => {
      if (document.hidden || !this.productCardInteractionIntervals.has(card)) return;
      this.#advanceProductCardSlide(slideshow);
    }, 1800);

    this.productCardInteractionIntervals.set(card, interval);
  }

  /**
   * @param {Element} card
   */
  #stopProductCardInteraction(card) {
    const slideshow = /** @type {HTMLElement & { suspend?: () => void }} */ (
      card.querySelector('.card-gallery slideshow-component')
    );
    if (!slideshow) return;

    const interval = this.productCardInteractionIntervals.get(card);
    if (interval !== undefined) {
      window.clearInterval(interval);
      this.productCardInteractionIntervals.delete(card);
    }

    slideshow.removeAttribute('data-kult-card-autoplay');
    slideshow.removeAttribute('aria-live');
    slideshow.suspend?.();
  }

  /**
   * @param {HTMLElement & { current?: number, slides?: Element[], select?: (input: number, event?: Event, options?: { animate?: boolean }) => void }} slideshow
   */
  #advanceProductCardSlide(slideshow) {
    if (!slideshow.select) return;

    const slideCount = slideshow.slides?.length || slideshow.querySelectorAll('slideshow-slide:not([hidden])').length;
    if (slideCount <= 1) return;

    const current = slideshow.current || 0;
    const next = current + 1 >= slideCount ? 0 : current + 1;
    slideshow.select(next, undefined, { animate: false });
  }

  /**
   * @param {PointerEvent} event
   */
  #handleProductCardPointerEnter = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof Element) || event.target !== card) return;
    if (event.pointerType !== 'mouse' || !mediaQueryLarge.matches) return;

    this.#startProductCardInteraction(card);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleProductCardPointerLeave = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof Element) || event.target !== card) return;
    if (event.pointerType !== 'mouse') return;

    this.#stopProductCardInteraction(card);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleProductCardPointerDown = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof Element)) return;
    if (event.pointerType !== 'touch' || mediaQueryLarge.matches) return;

    this.#clearProductCardTouchDelay(card);
    const delay = window.setTimeout(() => {
      this.productCardTouchDelays.delete(card);
      this.productCardTouchActivated.add(card);
      this.#startProductCardInteraction(card);
    }, 350);
    this.productCardTouchDelays.set(card, delay);
  };

  /**
   * @param {PointerEvent} event
   */
  #handleProductCardPointerEnd = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof Element) || event.pointerType !== 'touch') return;

    this.#clearProductCardTouchDelay(card);
    this.#stopProductCardInteraction(card);

    if (this.productCardTouchActivated.has(card)) {
      window.setTimeout(() => this.productCardTouchActivated.delete(card), 600);
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #handleProductCardClick = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof Element) || !this.productCardTouchActivated.has(card)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.productCardTouchActivated.delete(card);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleProductCardContextMenu = (event) => {
    const card = event.currentTarget;
    if (!(card instanceof Element)) return;
    if (!this.productCardTouchDelays.has(card) && !this.productCardTouchActivated.has(card)) return;

    event.preventDefault();
  };

  /**
   * @param {Element} card
   */
  #clearProductCardTouchDelay(card) {
    const delay = this.productCardTouchDelays.get(card);
    if (delay === undefined) return;

    window.clearTimeout(delay);
    this.productCardTouchDelays.delete(card);
  }

  /**
   * @param {Element} card
   */
  #removeProductCardInteractions(card) {
    this.#clearProductCardTouchDelay(card);
    this.#stopProductCardInteraction(card);
    this.productCardTouchActivated.delete(card);
    card.removeAttribute('data-kult-card-interaction-ready');
    card.querySelector('.card-gallery slideshow-component')?.removeAttribute('data-kult-card-interaction-managed');
    card.removeEventListener('pointerenter', this.#handleProductCardPointerEnter, true);
    card.removeEventListener('pointerleave', this.#handleProductCardPointerLeave, true);
    card.removeEventListener('pointerdown', this.#handleProductCardPointerDown);
    card.removeEventListener('pointerup', this.#handleProductCardPointerEnd);
    card.removeEventListener('pointercancel', this.#handleProductCardPointerEnd);
    card.removeEventListener('click', this.#handleProductCardClick, true);
    card.removeEventListener('contextmenu', this.#handleProductCardContextMenu);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    this.querySelectorAll('.product-grid__item').forEach((card) => {
      this.#clearProductCardTouchDelay(card);
      this.#stopProductCardInteraction(card);
      this.productCardTouchActivated.delete(card);
    });

    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
